import React, { useCallback, useEffect, useState } from "react";
import { ArrowUpRight, Link2, RotateCcw, TriangleAlert, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, fmtDateShort } from "../../lib/api";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { FabricOrphanRecoveryDialog } from "./FabricOrphanRecoveryDialog";

const num = (value) => (Number(value) || 0).toLocaleString("id-ID", {
  maximumFractionDigits: 2,
});

const lotsLabel = (lots) => lots.map((lot) => (
  `${lot.lot || "Tanpa lot"}: ${num(lot.qty)} yd`
)).join(" · ");

export const FabricCuttingReferencesDialog = ({ canRecover, color, onClose, onRecovered }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const navigate = useNavigate();

  const loadReferences = useCallback(async () => {
    if (!color?.id) {
      return;
    }
    setData(null);
    setError("");
    try {
      const response = await api.get(`/production/fabrics/color/${color.id}/cutting-references`, {
        timeout: 15000,
      });
      setData(response.data);
    } catch (requestError) {
      const message = formatApiError(requestError.response?.data?.detail);
      setError(message || "Referensi Cutting tidak dapat dimuat.");
      toast.error(message || "Referensi Cutting tidak dapat dimuat.");
    }
  }, [color?.id]);

  useEffect(() => {
    loadReferences();
  }, [loadReferences]);

  const openCutting = (cuttingId) => {
    onClose();
    navigate(`/production/cutting?focus=${encodeURIComponent(cuttingId)}`);
  };

  if (!color) {
    return null;
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        className="max-h-[90vh] max-w-5xl overflow-y-auto"
        data-testid="fabric-cutting-references-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-cocoa">
            <Link2 className="h-5 w-5" />
            Referensi Cutting Aktif
          </DialogTitle>
          <DialogDescription>
            {color.fabricName} · {color.colorName}
          </DialogDescription>
        </DialogHeader>
        {!data && !error ? (
          <div className="py-12 text-center text-wmuted" data-testid="fabric-cutting-references-loading">
            Memuat referensi Cutting...
          </div>
        ) : error ? (
          <div className="space-y-3 py-8 text-center" data-testid="fabric-cutting-references-error">
            <div className="text-sm text-status-reject">{error}</div>
            <Button
              data-testid="fabric-cutting-references-retry"
              onClick={loadReferences}
              variant="outline"
            >
              <RotateCcw className="mr-1.5 h-4 w-4" />
              Coba Lagi
            </Button>
          </div>
        ) : (
          <>
            <div className="grid gap-3 border-y border-taupe/20 py-3 sm:grid-cols-2">
              <div>
                <div className="text-xs text-wmuted">Cutting aktif terkait</div>
                <div className="text-xl font-bold text-cocoa" data-testid="fabric-cutting-reference-count">
                  {data.references.length} dokumen
                </div>
              </div>
              <div>
                <div className="text-xs text-wmuted">Kain masih di Cutting</div>
                <div className="text-xl font-bold text-cocoa" data-testid="fabric-cutting-reference-yard">
                  {num(data.total_active_yard)} yd
                </div>
              </div>
            </div>
            {data.references.length > 0 ? (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Cutting</TableHead>
                        <TableHead>Rencana Produksi</TableHead>
                        <TableHead>Produk</TableHead>
                        <TableHead>Roll / Qty</TableHead>
                        <TableHead>PIC</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Tanggal</TableHead>
                        <TableHead className="text-right">Aksi</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.references.map((reference) => (
                        <TableRow
                          data-testid={`fabric-cutting-reference-row-${reference.cutting_id}`}
                          key={reference.cutting_id}
                        >
                          <TableCell className="font-mono text-xs text-cocoa">
                            {reference.cutting_number}
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {reference.plan_number}
                          </TableCell>
                          <TableCell className="font-medium text-cocoa">
                            {reference.product_name}
                          </TableCell>
                          <TableCell className="text-xs">
                            {lotsLabel(reference.lots)}
                          </TableCell>
                          <TableCell>{reference.pic}</TableCell>
                          <TableCell>{reference.status}</TableCell>
                          <TableCell>{fmtDateShort(reference.created_at)}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              data-testid={`fabric-cutting-reference-open-${reference.cutting_id}`}
                              onClick={() => openCutting(reference.cutting_id)}
                              size="sm"
                              variant="outline"
                            >
                              Buka Detail
                              <ArrowUpRight className="ml-1.5 h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <div className="divide-y divide-taupe/20 md:hidden">
                  {data.references.map((reference) => (
                    <div
                      className="space-y-2 py-4"
                      data-testid={`fabric-cutting-reference-mobile-row-${reference.cutting_id}`}
                      key={reference.cutting_id}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-mono text-xs text-cocoa">
                            {reference.cutting_number}
                          </div>
                          <div className="mt-1 text-sm font-medium text-cocoa">
                            {reference.product_name}
                          </div>
                        </div>
                        <div className="text-right text-xs text-wmuted">{reference.status}</div>
                      </div>
                      <div className="text-xs text-wmuted">
                        Rencana {reference.plan_number} · PIC {reference.pic}
                      </div>
                      <div className="text-xs text-wmuted">{lotsLabel(reference.lots)}</div>
                      <Button
                        className="w-full"
                        data-testid={`fabric-cutting-reference-mobile-open-${reference.cutting_id}`}
                        onClick={() => openCutting(reference.cutting_id)}
                        size="sm"
                        variant="outline"
                      >
                        Buka Detail Cutting
                        <ArrowUpRight className="ml-1.5 h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="py-8 text-center text-wmuted" data-testid="fabric-cutting-references-empty">
                Tidak ada dokumen Cutting aktif yang masih menahan kain ini.
              </div>
            )}
            {data.unlinked_yards.length > 0 && (
              <div
                className="border-l-4 border-amber-500 bg-amber-50 p-3 text-sm text-amber-900"
                data-testid="fabric-cutting-unlinked-warning"
              >
                <div className="flex items-center gap-2 font-semibold">
                  <TriangleAlert className="h-4 w-4" />
                  Ada {num(data.total_unlinked_yard)} yd di bucket Cutting tanpa dokumen aktif
                </div>
                <div className="mt-1 text-xs">
                  {data.unlinked_yards.map((row) => (
                    <div key={`${row.warehouse_id}-${row.lot}`}>
                      Lot {row.lot || "tanpa lot"}: {num(row.qty)} yd · Referensi terakhir
                      {" "}{row.last_reference || "-"}
                    </div>
                  ))}
                </div>
                {canRecover && (
                  <Button
                    className="mt-3"
                    data-testid="fabric-cutting-recovery-open"
                    onClick={() => setRecoveryOpen(true)}
                    size="sm"
                    variant="outline"
                  >
                    <RotateCcw className="mr-1.5 h-4 w-4" />
                    Pulihkan Orphan ke Available
                  </Button>
                )}
              </div>
            )}
          </>
        )}
        <div className="flex justify-end">
          <Button
            data-testid="fabric-cutting-references-close"
            onClick={onClose}
            variant="outline"
          >
            <X className="mr-1.5 h-4 w-4" />
            Tutup
          </Button>
        </div>
      </DialogContent>
      {recoveryOpen && (
        <FabricOrphanRecoveryDialog
          color={color}
          onClose={() => setRecoveryOpen(false)}
          onRecovered={async () => {
            await loadReferences();
            await onRecovered?.();
          }}
        />
      )}
    </Dialog>
  );
};